import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Mail, MailWarning } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { businessType as businessTypeSchema, tradeTemplate as tradeTemplateSchema, type BusinessType, type TradeTemplate } from '@buildbee/contracts';
import { Alert, AuthLayout, Button, Form, FormError, FormField, Input, LoadingState, useZodForm } from '@buildbee/ui';
import { authClient } from '../../../lib/auth-client';
import { SESSION_QUERY_KEY } from '../../../router/parents';
import { Wordmark } from '../../../shell/Logo';

/**
 * Email verification for password sign-ups. Better Auth sends the link (to /verify-email on this
 * app) on sign-up and again when an unverified account tries to sign in with its password. The
 * company details entered at sign-up are kept in this browser until the workspace is created, so
 * onboarding after verification does not ask for them twice.
 */

const PENDING_KEY = 'buildbee.pendingWorkspace';
const pendingSchema = z.object({
  name: z.string().trim().min(1).max(120),
  businessType: businessTypeSchema,
  trade: tradeTemplateSchema.optional(),
  country: z.enum(['CA', 'US']).optional(),
  region: z.string().trim().max(60).optional(),
});
/** Company details typed at sign-up, including where the business is (sets currency defaults and sales tax). */
export type PendingWorkspace = { name: string; businessType: BusinessType; trade?: TradeTemplate; country?: 'CA' | 'US'; region?: string };

/** Remember the company details from sign-up (best effort: storage may be unavailable). */
export function savePendingWorkspace(value: PendingWorkspace): void {
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify(value));
  } catch {
    // Private mode or blocked storage: onboarding simply asks again.
  }
}

export function loadPendingWorkspace(): PendingWorkspace | null {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = pendingSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function clearPendingWorkspace(): void {
  try {
    window.localStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
}

/** Where to go after verifying: back into the app, or onboarding when a workspace is still to be created. */
export function afterVerifyTarget(next: string | undefined, pending: PendingWorkspace | null): string {
  if (pending) return '/onboarding';
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

/** Absolute URL Better Auth carries into the verification link (only its path is used). */
function verifyCallbackURL(): string {
  return `${window.location.origin}/onboarding`;
}

const RESEND_COOLDOWN_SECONDS = 60;

/** "Resend verification email" with a cooldown (the API also rate limits it per IP). */
export function ResendVerification({ email: initialEmail, askEmail = false }: { email?: string; askEmail?: boolean }) {
  const [cooldown, setCooldown] = useState(0);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useZodForm(z.object({ email: z.email('Enter a valid email') }), { defaultValues: { email: initialEmail ?? '' } });

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  const resend = async (address: string) => {
    setError(null);
    const res = await authClient.sendVerificationEmail({ email: address, callbackURL: verifyCallbackURL() });
    if (res.error) {
      setError(res.error.status === 429 ? 'Too many requests. Please wait a minute and try again.' : (res.error.message ?? 'Could not send the email'));
      return;
    }
    setSent(true);
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  if (askEmail || !initialEmail) {
    return (
      <Form form={form} onSubmit={({ email }) => resend(email)}>
        <FormField name="email" label="Email" description="We will send a new verification link.">
          <Input type="email" autoComplete="email" />
        </FormField>
        {sent ? <Alert variant="success" title="Sent">If that address has an account waiting for verification, a new link is on its way.</Alert> : null}
        <FormError message={error} />
        <Button type="submit" className="w-full" loading={form.formState.isSubmitting} disabled={cooldown > 0}>
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Send a new link'}
        </Button>
      </Form>
    );
  }

  return (
    <div className="space-y-2">
      {sent ? <p className="text-sm text-success">A new link is on its way.</p> : null}
      <FormError message={error} />
      <Button variant="outline" className="w-full" disabled={cooldown > 0} onClick={() => void resend(initialEmail)}>
        <Mail /> {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend verification email'}
      </Button>
    </div>
  );
}

/** Shown after a password sign-up, or when an unverified account tries to sign in. */
export function VerifyEmailNotice({ email, resent = false }: { email?: string; resent?: boolean }) {
  return (
    <AuthLayout
      logo={<Wordmark />}
      title="Verify your email"
      description={email ? `We sent a verification link to ${email}.` : 'We sent you a verification link.'}
      footer={<Link to="/sign-in">Back to sign in</Link>}
    >
      <div className="space-y-4 text-center text-sm text-muted-foreground">
        <Mail className="mx-auto size-10" />
        <p>
          {resent
            ? 'Your email is not verified yet, so we sent a new link. '
            : 'Open the link in that email to activate your account. '}
          It expires in 24 hours. Check your spam folder if it does not arrive within a few minutes.
        </p>
        <ResendVerification email={email} />
      </div>
    </AuthLayout>
  );
}

type VerifyState = { status: 'verifying' } | { status: 'done' } | { status: 'error'; message: string; expired: boolean };

/** Target of the verification link: confirms the address, signs the user in, continues. */
export function VerifyEmailPage({ token, next }: { token?: string; next?: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState<VerifyState>(token ? { status: 'verifying' } : { status: 'error', message: 'This link is missing its token.', expired: false });
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    void (async () => {
      const res = await authClient.verifyEmail({ query: { token } });
      if (res.error) {
        const code = (res.error as { code?: string }).code ?? '';
        const expired = /expired/i.test(code) || /expired/i.test(res.error.message ?? '');
        setState({
          status: 'error',
          expired,
          message: expired ? 'This verification link has expired.' : 'This verification link is invalid or was already used.',
        });
        return;
      }
      setState({ status: 'done' });
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      await navigate({ to: afterVerifyTarget(next, loadPendingWorkspace()) });
    })();
  }, [token, next, navigate, queryClient]);

  if (state.status === 'verifying') return <LoadingState className="min-h-dvh" label="Verifying your email" />;
  if (state.status === 'done') {
    return (
      <AuthLayout logo={<Wordmark />} title="Email verified">
        <div className="space-y-4 text-center text-sm text-muted-foreground">
          <CheckCircle2 className="mx-auto size-10 text-success" />
          <p>Taking you to BuildBee...</p>
        </div>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout logo={<Wordmark />} title="Could not verify your email" footer={<Link to="/sign-in">Back to sign in</Link>}>
      <div className="space-y-4">
        <Alert variant="warning" title={state.message}>
          <span className="inline-flex items-center gap-1">
            <MailWarning className="size-4" /> Enter your email to get a new link. If you already verified, just sign in.
          </span>
        </Alert>
        <ResendVerification askEmail />
      </div>
    </AuthLayout>
  );
}
